import { deviceStatusCode } from '@/utils/deviceStatus';

import type { EnrollmentDeviceInput } from './evaluateEnrollmentChecklist';
import type { EnrollmentChecklistToggles } from '@/types/enrollment';

const TOGGLE_KEYS: (keyof EnrollmentChecklistToggles)[] = [
  'department',
  'deviceAdded',
  'diagnosis',
  'provider',
  'demographics',
  'insurance'
];

export interface OrgEnrollmentSettings {
  enrollmentChecklist: EnrollmentChecklistToggles | null;
  ffsDeviceTypes: string[] | null;
}

export type EnrollmentSettingsSource = 'clinic' | 'account' | 'clinic+account' | 'defaults';

export interface ResolvedEnrollmentSettings {
  enrollmentChecklist: EnrollmentChecklistToggles;
  ffsDeviceTypes: string[];
  source: EnrollmentSettingsSource;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

/**
 * The stored `settings` from `GET /organizations/:id/settings/full` (flat, with `settings`),
 * from `{ organization: { settings } }`, or a settings object itself.
 */
export function enrollmentSettingsFrom(payload: unknown): OrgEnrollmentSettings | null {
  const root = asRecord(payload);
  if (!root) return null;
  const organization = asRecord(root.organization);
  const settings =
    asRecord(organization?.settings) ??
    asRecord(root.settings) ??
    (root.enrollmentChecklist !== undefined || root.billing !== undefined ? root : null);
  if (!settings) return { enrollmentChecklist: null, ffsDeviceTypes: null };

  const rawToggles = asRecord(settings.enrollmentChecklist);
  let enrollmentChecklist: EnrollmentChecklistToggles | null = null;
  if (rawToggles) {
    enrollmentChecklist = {};
    TOGGLE_KEYS.forEach((key) => {
      if (typeof rawToggles[key] === 'boolean') {
        (enrollmentChecklist as EnrollmentChecklistToggles)[key] = rawToggles[key] as boolean;
      }
    });
  }
  const billing = asRecord(settings.billing);
  const ffsDeviceTypes = Array.isArray(billing?.ffsDeviceTypes)
    ? (billing.ffsDeviceTypes as unknown[]).filter(
        (value): value is string => typeof value === 'string' && value.trim().length > 0
      )
    : null;
  return { enrollmentChecklist, ffsDeviceTypes };
}

export interface HfSchedulingSettings {
  enabled?: boolean;
  manufacturers?: string[];
}

/** `settings.scheduling` from the same settings/full payload the checklist already loads. */
export function hfSchedulingFrom(payload: unknown): HfSchedulingSettings | null {
  const root = asRecord(payload);
  if (!root) return null;
  const organization = asRecord(root.organization);
  const settings =
    asRecord(organization?.settings) ??
    asRecord(root.settings) ??
    (root.scheduling !== undefined ? root : null);
  const scheduling = asRecord(settings?.scheduling);
  if (!scheduling) return null;
  const enabled =
    typeof scheduling.isHfMonitoringEnabled === 'boolean'
      ? scheduling.isHfMonitoringEnabled
      : undefined;
  const manufacturers = Array.isArray(scheduling.hfManufacturers)
    ? scheduling.hfManufacturers.filter(
        (value): value is string => typeof value === 'string' && value.trim().length > 0
      )
    : undefined;
  if (enabled === undefined && manufacturers === undefined) return null;
  return { enabled, manufacturers };
}

/** Clinic value wins, then the parent account. HF stays off until a clinic turns it on. */
export function resolveHfMonitoring(
  clinic: HfSchedulingSettings | null,
  account: HfSchedulingSettings | null
): { enabled: boolean; manufacturers: string[] | null } {
  return {
    enabled: clinic?.enabled ?? account?.enabled ?? false,
    manufacturers: clinic?.manufacturers ?? account?.manufacturers ?? null
  };
}

/**
 * Clinic first, then the parent account, key by key — the same order synergy's
 * `Organization.getSetting` uses. A key neither org sets falls back to the default (on).
 */
export function resolveEnrollmentSettings(
  clinic: OrgEnrollmentSettings | null,
  account: OrgEnrollmentSettings | null
): ResolvedEnrollmentSettings {
  const toggles: EnrollmentChecklistToggles = {};
  let fromClinic = false;
  let fromAccount = false;
  TOGGLE_KEYS.forEach((key) => {
    const own = clinic?.enrollmentChecklist?.[key];
    const inherited = account?.enrollmentChecklist?.[key];
    if (typeof own === 'boolean') {
      toggles[key] = own;
      fromClinic = true;
    } else if (typeof inherited === 'boolean') {
      toggles[key] = inherited;
      fromAccount = true;
    }
  });
  let ffsDeviceTypes: string[] = [];
  if (clinic?.ffsDeviceTypes) {
    ffsDeviceTypes = clinic.ffsDeviceTypes;
    fromClinic = true;
  } else if (account?.ffsDeviceTypes) {
    ffsDeviceTypes = account.ffsDeviceTypes;
    fromAccount = true;
  }
  const source: EnrollmentSettingsSource =
    fromClinic && fromAccount
      ? 'clinic+account'
      : fromClinic
        ? 'clinic'
        : fromAccount
          ? 'account'
          : 'defaults';
  return { enrollmentChecklist: toggles, ffsDeviceTypes, source };
}

interface OrgLike {
  billingType?: string | null;
  ehrIntegration?: boolean | null;
  enableMultipleDiagnosesBilling?: boolean | null;
  parentId?: string | null;
  parent?: {
    id?: string | null;
    billingType?: string | null;
    ehrIntegration?: boolean | null;
    enableMultipleDiagnosesBilling?: boolean | null;
  } | null;
}

/** Parent account first, then the clinic. */
export function accountBillingType(org?: OrgLike | null): string | null {
  return org?.parent?.billingType || org?.billingType || null;
}

/** Parent account first, then the clinic. */
export function accountEhrIntegrated(org?: OrgLike | null): boolean {
  const parent = org?.parent?.ehrIntegration;
  if (typeof parent === 'boolean') return parent;
  return Boolean(org?.ehrIntegration);
}

/**
 * Multiple diagnosis codes. When the clinic belongs to an account, only the account flag counts.
 * Unset is off.
 */
export function accountMultipleDiagnoses(org?: OrgLike | null): boolean {
  if (!org) return false;
  const hasParent = Boolean(org.parentId || org.parent?.id || org.parent);
  if (hasParent) return org.parent?.enableMultipleDiagnosesBilling === true;
  return org.enableMultipleDiagnosesBilling === true;
}

export interface DeviceLike {
  id: string;
  vendor?: string | null;
  type?: string | null;
  modelName?: string | null;
  modelNumber?: string | null;
  serialNumber?: string | null;
  lastTransmissionAt?: string | null;
  status?: string | number | null;
  createdAt?: string | null;
}

const STATUS_RANK: Record<number, number> = { 1: 0, 2: 1, 3: 2 };

/**
 * The device this enrollment is for: the task's device when it is on the patient, else the
 * patient's active device, then new implant, then pending (newest first). Inactive and
 * entered-in-error devices are skipped.
 */
export function pickEnrollmentDevice<T extends DeviceLike>(
  devices: T[] | null | undefined,
  preferredId?: string | null
): T | null {
  const list = devices ?? [];
  if (preferredId) {
    const preferred = list.find((device) => device.id === preferredId);
    if (preferred && deviceStatusCode(preferred.status) !== 4) return preferred;
  }
  const candidates = list
    .filter((device) => {
      const code = deviceStatusCode(device.status);
      return code === null || code in STATUS_RANK;
    })
    .slice()
    .sort((a, b) => {
      const rank = (device: T) => STATUS_RANK[deviceStatusCode(device.status) ?? 1] ?? 3;
      const byStatus = rank(a) - rank(b);
      if (byStatus !== 0) return byStatus;
      return String(b.createdAt ?? '').localeCompare(String(a.createdAt ?? ''));
    });
  return candidates[0] ?? null;
}

/** Every active, new-implant, or pending device. Inactive and entered-in-error are skipped. */
export function activeEnrollmentDevices<T extends DeviceLike>(
  devices: T[] | null | undefined
): T[] {
  return (devices ?? [])
    .filter((device) => {
      const code = deviceStatusCode(device.status);
      return code === null || code in STATUS_RANK;
    })
    .slice()
    .sort((a, b) => {
      const rank = (device: T) => STATUS_RANK[deviceStatusCode(device.status) ?? 1] ?? 3;
      const byStatus = rank(a) - rank(b);
      if (byStatus !== 0) return byStatus;
      return String(b.createdAt ?? '').localeCompare(String(a.createdAt ?? ''));
    });
}

/** Newest inactive device, when nothing active is on the chart. Entered-in-error is skipped. */
export function pickInactiveEnrollmentDevice<T extends DeviceLike>(
  devices: T[] | null | undefined,
  preferredId?: string | null
): T | null {
  const inactive = (devices ?? []).filter((device) => deviceStatusCode(device.status) === 0);
  if (preferredId) {
    const preferred = inactive.find((device) => device.id === preferredId);
    if (preferred) return preferred;
  }
  return (
    inactive
      .slice()
      .sort((a, b) => String(b.createdAt ?? '').localeCompare(String(a.createdAt ?? '')))[0] ?? null
  );
}

export function enrollmentDeviceInput(
  device: DeviceLike | null,
  vendorName?: (vendor: string) => string
): EnrollmentDeviceInput | null {
  if (!device) return null;
  const vendor = device.vendor ?? '';
  return {
    id: device.id,
    vendor,
    vendorName: vendor && vendorName ? vendorName(vendor) : vendor,
    type: device.type ?? null,
    modelName: device.modelName ?? null,
    modelNumber: device.modelNumber ?? null,
    serialNumber: device.serialNumber ?? null,
    lastTransmissionAt: device.lastTransmissionAt ?? null
  };
}
